// The following code is style-only so regressions will not be caught by a Jest unit test
/* istanbul ignore file */
import { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { Space } from 'src/utils/theme';

const isPercentage = (s: string): boolean => s[s.length - 1] === '%';

export interface WithSidebarProps {
  /**
   * Default width (flex-basis) of the sidebar.
   * Leave blank for intrinsic.
   */
  $sidebarWidth?: Space;

  /**
   * Gap between sidebar and content.
   */
  gap?: Space;

  /**
   * Minimum percentage width for the content section.
   * The component will wrap when the content is forced to
   * be smaller than this.
   */
  $contentMinWidth?: string;

  /**
   * Whether to flip the component so that sidebar is on the
   * right and content on left
   */
  $flipSides?: boolean;

  /**
   * Must have a single child which has 2 children (see example)
   */
  children: ReactElement;
}

/**
 * Positions one piece of content next to another
 * with intelligent wrapping behaviour.
 *
 * @example
 * <WithSidebar gap='s1' sidebarWidth='s4' contentMinWidth='20%' >
 *   <form>
 *    <input/>
 *    <button />
 *   </form>
 * <WithSidebar>
 */
const WithSidebar = styled.div<WithSidebarProps>(
  ({ $sidebarWidth, gap = 's0', $contentMinWidth = '50%', $flipSides = false, theme }) => {
    if (!isPercentage($contentMinWidth)) {
      throw new Error('contentMin must be percentage');
    }
    const gapValue = theme.sz[gap];
    const sidebarWidthValue = !$sidebarWidth ? '0' : theme.sz[$sidebarWidth];
    // prettier-ignore
    return css`
      width: 100%;

      & > * {
        display: flex;
        flex-wrap: wrap;
        margin: -${gapValue}; /* negative margin hack to allow gap that still wraps okay */
      }

      /* intermediary wrapper */
      & > * > * {
        margin: ${gapValue};
      }

      & > * > *:${$flipSides ? 'last-child' : 'first-child'} {
        flex-grow: 1; /* makes the sidebar grow after wrap */
        ${$sidebarWidth && `flex-basis: ${sidebarWidthValue};`}
      }

      & > * > *:${$flipSides ? 'first-child' : 'last-child'} {
        flex-basis: 0; /* needed so the content does not force wrap instantly */
        flex-grow: 999; /* make content take up remaining space */
        min-width: calc(${$contentMinWidth} - (${gapValue} * 2));
      }
    `;
  },
);

export default WithSidebar;
